import { useEffect, useState, type FormEvent } from "react";
import type { CategoriaEconomica, NaturalezaCategoriaEconomica, SubcategoriaEconomica } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Campo, Input, Select } from "../../components/Formulario";
import { Insignia } from "../../components/Insignia";
import { Modal } from "../../components/Modal";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

const NATURALEZAS: NaturalezaCategoriaEconomica[] = ["Ingreso", "Gasto"];

// Administración de Categorías/Subcategorías económicas — mismo criterio
// visual que PanelCatalogoEstudio.tsx, pero con dos niveles (Categoría
// pertenece a una Naturaleza; Subcategoría pertenece a una Categoría). Ver
// migrations/0010_categorias_economicas.sql.
export function PanelCategoriasEconomicas({ puedeAdministrar }: { puedeAdministrar: boolean }) {
  const { mostrar } = useToast();

  // --- Categorías ---
  const [categorias, setCategorias] = useState<CategoriaEconomica[]>([]);
  const [cargandoCategorias, setCargandoCategorias] = useState(true);
  const [modalCategoriaAbierto, setModalCategoriaAbierto] = useState(false);
  const [categoriaEnEdicion, setCategoriaEnEdicion] = useState<CategoriaEconomica | null>(null);
  const [valoresCategoria, setValoresCategoria] = useState({
    naturaleza: "Gasto" as NaturalezaCategoriaEconomica,
    nombre: "",
    descripcion: "",
    orden: "0",
  });
  const [guardandoCategoria, setGuardandoCategoria] = useState(false);

  function cargarCategorias() {
    setCargandoCategorias(true);
    api
      .get<{ items: CategoriaEconomica[] }>("/economia-catalogos/categorias")
      .then((d) => setCategorias(d.items))
      .catch(() => mostrar("No se pudieron cargar las categorías.", "error"))
      .finally(() => setCargandoCategorias(false));
  }

  useEffect(cargarCategorias, []); // eslint-disable-line react-hooks/exhaustive-deps

  function abrirAltaCategoria() {
    setCategoriaEnEdicion(null);
    setValoresCategoria({ naturaleza: "Gasto", nombre: "", descripcion: "", orden: String(categorias.length + 1) });
    setModalCategoriaAbierto(true);
  }

  function abrirEdicionCategoria(cat: CategoriaEconomica) {
    setCategoriaEnEdicion(cat);
    setValoresCategoria({
      naturaleza: cat.naturaleza,
      nombre: cat.nombre,
      descripcion: cat.descripcion ?? "",
      orden: String(cat.orden),
    });
    setModalCategoriaAbierto(true);
  }

  async function guardarCategoria(e: FormEvent) {
    e.preventDefault();
    setGuardandoCategoria(true);
    try {
      if (categoriaEnEdicion) {
        await api.put(`/economia-catalogos/categorias/${categoriaEnEdicion.id}`, {
          nombre: valoresCategoria.nombre,
          descripcion: valoresCategoria.descripcion || null,
          orden: Number(valoresCategoria.orden),
        });
      } else {
        await api.post("/economia-catalogos/categorias", {
          naturaleza: valoresCategoria.naturaleza,
          nombre: valoresCategoria.nombre,
          descripcion: valoresCategoria.descripcion || null,
          orden: Number(valoresCategoria.orden),
        });
      }
      mostrar("Categoría guardada.", "exito");
      setModalCategoriaAbierto(false);
      cargarCategorias();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar la categoría.", "error");
    } finally {
      setGuardandoCategoria(false);
    }
  }

  async function alternarActivoCategoria(cat: CategoriaEconomica) {
    try {
      await api.patch(`/economia-catalogos/categorias/${cat.id}/activo`, { activo: !cat.activo });
      cargarCategorias();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  const columnasCategorias: ColumnaTabla<CategoriaEconomica>[] = [
    { encabezado: "Naturaleza", render: (c) => <Insignia texto={c.naturaleza} tipo="estado" /> },
    { encabezado: "Nombre", render: (c) => c.nombre },
    { encabezado: "Descripción", render: (c) => c.descripcion ?? "—" },
    { encabezado: "Orden", render: (c) => c.orden },
    { encabezado: "Estado", render: (c) => <Insignia texto={c.activo ? "Activo" : "Inactivo"} /> },
    {
      encabezado: "",
      render: (c) =>
        puedeAdministrar ? (
          <div className="flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => abrirEdicionCategoria(c)}>
              Editar
            </Boton>
            <Boton variante="secundario" onClick={() => alternarActivoCategoria(c)}>
              {c.activo ? "Desactivar" : "Activar"}
            </Boton>
          </div>
        ) : null,
      className: "text-right",
    },
  ];

  // --- Subcategorías (de una categoría elegida) ---
  const [categoriaIdSeleccionada, setCategoriaIdSeleccionada] = useState("");
  const [subcategorias, setSubcategorias] = useState<SubcategoriaEconomica[]>([]);
  const [cargandoSubcategorias, setCargandoSubcategorias] = useState(false);
  const [modalSubcategoriaAbierto, setModalSubcategoriaAbierto] = useState(false);
  const [subcategoriaEnEdicion, setSubcategoriaEnEdicion] = useState<SubcategoriaEconomica | null>(null);
  const [valoresSubcategoria, setValoresSubcategoria] = useState({ nombre: "", descripcion: "", orden: "0" });
  const [guardandoSubcategoria, setGuardandoSubcategoria] = useState(false);

  function cargarSubcategorias(categoriaId: string) {
    if (!categoriaId) {
      setSubcategorias([]);
      return;
    }
    setCargandoSubcategorias(true);
    api
      .get<{ items: SubcategoriaEconomica[] }>(`/economia-catalogos/subcategorias?categoria_id=${categoriaId}`)
      .then((d) => setSubcategorias(d.items))
      .catch(() => mostrar("No se pudieron cargar las subcategorías.", "error"))
      .finally(() => setCargandoSubcategorias(false));
  }

  useEffect(() => cargarSubcategorias(categoriaIdSeleccionada), [categoriaIdSeleccionada]); // eslint-disable-line react-hooks/exhaustive-deps

  function abrirAltaSubcategoria() {
    setSubcategoriaEnEdicion(null);
    setValoresSubcategoria({ nombre: "", descripcion: "", orden: String(subcategorias.length + 1) });
    setModalSubcategoriaAbierto(true);
  }

  function abrirEdicionSubcategoria(sub: SubcategoriaEconomica) {
    setSubcategoriaEnEdicion(sub);
    setValoresSubcategoria({ nombre: sub.nombre, descripcion: sub.descripcion ?? "", orden: String(sub.orden) });
    setModalSubcategoriaAbierto(true);
  }

  async function guardarSubcategoria(e: FormEvent) {
    e.preventDefault();
    setGuardandoSubcategoria(true);
    try {
      if (subcategoriaEnEdicion) {
        await api.put(`/economia-catalogos/subcategorias/${subcategoriaEnEdicion.id}`, {
          nombre: valoresSubcategoria.nombre,
          descripcion: valoresSubcategoria.descripcion || null,
          orden: Number(valoresSubcategoria.orden),
        });
      } else {
        await api.post("/economia-catalogos/subcategorias", {
          categoria_id: categoriaIdSeleccionada,
          nombre: valoresSubcategoria.nombre,
          descripcion: valoresSubcategoria.descripcion || null,
          orden: Number(valoresSubcategoria.orden),
        });
      }
      mostrar("Subcategoría guardada.", "exito");
      setModalSubcategoriaAbierto(false);
      cargarSubcategorias(categoriaIdSeleccionada);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar la subcategoría.", "error");
    } finally {
      setGuardandoSubcategoria(false);
    }
  }

  async function alternarActivoSubcategoria(sub: SubcategoriaEconomica) {
    try {
      await api.patch(`/economia-catalogos/subcategorias/${sub.id}/activo`, { activo: !sub.activo });
      cargarSubcategorias(categoriaIdSeleccionada);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  const columnasSubcategorias: ColumnaTabla<SubcategoriaEconomica>[] = [
    { encabezado: "Nombre", render: (s) => s.nombre },
    { encabezado: "Descripción", render: (s) => s.descripcion ?? "—" },
    { encabezado: "Orden", render: (s) => s.orden },
    { encabezado: "Estado", render: (s) => <Insignia texto={s.activo ? "Activo" : "Inactivo"} /> },
    {
      encabezado: "",
      render: (s) =>
        puedeAdministrar ? (
          <div className="flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => abrirEdicionSubcategoria(s)}>
              Editar
            </Boton>
            <Boton variante="secundario" onClick={() => alternarActivoSubcategoria(s)}>
              {s.activo ? "Desactivar" : "Activar"}
            </Boton>
          </div>
        ) : null,
      className: "text-right",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-texto">Categorías económicas</h3>
            <p className="text-xs text-texto-secundario">
              Clasificación de Ingresos y Gastos para los movimientos económicos del estudio.
            </p>
          </div>
          {puedeAdministrar && <Boton onClick={abrirAltaCategoria}>+ Nueva categoría</Boton>}
        </div>
        <Tabla
          columnas={columnasCategorias}
          filas={categorias}
          claveFila={(c) => c.id}
          cargando={cargandoCategorias}
          tituloVacio="Sin categorías"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-texto">Subcategorías</h3>
            <p className="text-xs text-texto-secundario">Elegí una categoría para administrar sus subcategorías.</p>
          </div>
          {puedeAdministrar && categoriaIdSeleccionada && (
            <Boton onClick={abrirAltaSubcategoria}>+ Nueva subcategoría</Boton>
          )}
        </div>
        <Select value={categoriaIdSeleccionada} onChange={(e) => setCategoriaIdSeleccionada(e.target.value)} className="max-w-sm">
          <option value="">Seleccionar categoría…</option>
          {categorias.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.naturaleza} — {cat.nombre}
            </option>
          ))}
        </Select>
        {categoriaIdSeleccionada && (
          <Tabla
            columnas={columnasSubcategorias}
            filas={subcategorias}
            claveFila={(s) => s.id}
            cargando={cargandoSubcategorias}
            tituloVacio="Sin subcategorías"
          />
        )}
      </div>

      <Modal
        titulo={categoriaEnEdicion ? "Editar categoría" : "Nueva categoría"}
        abierto={modalCategoriaAbierto}
        onCerrar={() => setModalCategoriaAbierto(false)}
      >
        <form onSubmit={guardarCategoria} className="space-y-4">
          <Campo etiqueta="Naturaleza" obligatorio>
            <Select
              value={valoresCategoria.naturaleza}
              onChange={(e) =>
                setValoresCategoria((v) => ({ ...v, naturaleza: e.target.value as NaturalezaCategoriaEconomica }))
              }
              disabled={Boolean(categoriaEnEdicion)}
            >
              {NATURALEZAS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
            {categoriaEnEdicion && (
              <p className="mt-1 text-xs text-texto-secundario">
                La naturaleza no se puede cambiar una vez creada. Desactivá esta categoría y creá una nueva si
                necesitás otra naturaleza.
              </p>
            )}
          </Campo>
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={valoresCategoria.nombre}
              onChange={(e) => setValoresCategoria((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Descripción">
            <Input
              value={valoresCategoria.descripcion}
              onChange={(e) => setValoresCategoria((v) => ({ ...v, descripcion: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Orden">
            <Input
              type="number"
              value={valoresCategoria.orden}
              onChange={(e) => setValoresCategoria((v) => ({ ...v, orden: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalCategoriaAbierto(false)}
              disabled={guardandoCategoria}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoCategoria}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal
        titulo={subcategoriaEnEdicion ? "Editar subcategoría" : "Nueva subcategoría"}
        abierto={modalSubcategoriaAbierto}
        onCerrar={() => setModalSubcategoriaAbierto(false)}
      >
        <form onSubmit={guardarSubcategoria} className="space-y-4">
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={valoresSubcategoria.nombre}
              onChange={(e) => setValoresSubcategoria((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Descripción">
            <Input
              value={valoresSubcategoria.descripcion}
              onChange={(e) => setValoresSubcategoria((v) => ({ ...v, descripcion: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Orden">
            <Input
              type="number"
              value={valoresSubcategoria.orden}
              onChange={(e) => setValoresSubcategoria((v) => ({ ...v, orden: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalSubcategoriaAbierto(false)}
              disabled={guardandoSubcategoria}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoSubcategoria}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
